import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { useSearch } from '@/context/SearchContext'
import { cn } from '@/utils/cn'

/** @param {{ autoFocus?: boolean, onSubmit?: () => void, className?: string }} props */
export function SearchBar({ autoFocus = false, onSubmit, className }) {
  const { query, setQuery, reset } = useSearch()
  const inputRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (query.trim()) {
      navigate(`/shop?q=${encodeURIComponent(query.trim())}`)
      onSubmit?.()
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex h-11 items-center gap-2 rounded-full border border-charcoal-200 bg-white px-4 shadow-sm focus-within:border-coral focus-within:ring-2 focus-within:ring-coral/20',
        className
      )}
    >
      <Search size={18} className="text-charcoal-400" />
      {/*
        type="text", not "search": iOS Safari draws its own ⓧ cancel button
        and inset field chrome inside a search input, so the pill showed two
        clear buttons and a second border while typing. inputMode/enterKeyHint
        keep the phone keyboard's Search key.

        focus-visible:ring-0: the global `*:focus-visible` ring would land on
        this unrounded input and draw a coral rectangle inside the pill. The
        pill itself carries the focus state (focus-within above).
      */}
      <input
        ref={inputRef}
        type="text"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search baby products..."
        aria-label="Search products"
        className="h-full min-w-0 flex-1 appearance-none bg-transparent text-sm outline-none placeholder:text-charcoal-400 focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      {query && (
        <button
          type="button"
          onClick={reset}
          aria-label="Clear search"
          className="text-charcoal-400 hover:text-charcoal"
        >
          <X size={16} />
        </button>
      )}
    </form>
  )
}
